export type EveBackend = 'local' | 'staging'

export const STAGING_PROJECT_REF = 'ktkfhbuybiyubaesyhtt'
export const STAGING_SUPABASE_URL_DEFAULT = `https://${STAGING_PROJECT_REF}.supabase.co`

export interface EveSupabaseTarget {
  backend: EveBackend
  url: string
  anonKey: string
  projectId: string
}

const ENV_KEYS = [
  'VITE_EVE_BACKEND',
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_PUBLISHABLE_KEY',
  'VITE_SUPABASE_ANON_KEY',
  'VITE_SUPABASE_PROJECT_ID',
  'VITE_LOCAL_SUPABASE_URL',
  'VITE_LOCAL_SUPABASE_PUBLISHABLE_KEY',
  'VITE_LOCAL_SUPABASE_ANON_KEY',
  'VITE_LOCAL_SUPABASE_PROJECT_ID',
  'VITE_STAGING_SUPABASE_URL',
  'VITE_STAGING_SUPABASE_PUBLISHABLE_KEY',
  'VITE_STAGING_SUPABASE_ANON_KEY',
  'VITE_STAGING_SUPABASE_PROJECT_ID',
] as const

type EveEnvKey = (typeof ENV_KEYS)[number]
export type EveEnv = Partial<Record<EveEnvKey, string | undefined>>

function trim(value: string | undefined): string {
  return String(value ?? '').trim()
}

/**
 * Pick local vs persistent staging Supabase from `VITE_EVE_BACKEND`.
 * Staging is the hosted project `ktkfhbuybiyubaesyhtt` (auth, DB, functions, snapshots).
 */
export function resolveEveSupabaseTarget(env: EveEnv): EveSupabaseTarget {
  const backend: EveBackend = trim(env.VITE_EVE_BACKEND).toLowerCase() === 'staging'
    ? 'staging'
    : 'local'

  if (backend === 'staging') {
    const url = trim(env.VITE_STAGING_SUPABASE_URL) || STAGING_SUPABASE_URL_DEFAULT
    const anonKey = trim(env.VITE_STAGING_SUPABASE_ANON_KEY) || trim(env.VITE_STAGING_SUPABASE_PUBLISHABLE_KEY)
    const projectId = trim(env.VITE_STAGING_SUPABASE_PROJECT_ID) || STAGING_PROJECT_REF
    if (!anonKey) {
      throw new Error(
        'VITE_EVE_BACKEND=staging requires VITE_STAGING_SUPABASE_ANON_KEY (or VITE_STAGING_SUPABASE_PUBLISHABLE_KEY)',
      )
    }
    return { backend, url, anonKey, projectId }
  }

  return {
    backend,
    url: trim(env.VITE_LOCAL_SUPABASE_URL) || trim(env.VITE_SUPABASE_URL) || 'http://127.0.0.1:54321',
    anonKey:
      trim(env.VITE_LOCAL_SUPABASE_ANON_KEY)
      || trim(env.VITE_LOCAL_SUPABASE_PUBLISHABLE_KEY)
      || trim(env.VITE_SUPABASE_PUBLISHABLE_KEY)
      || trim(env.VITE_SUPABASE_ANON_KEY),
    projectId: trim(env.VITE_LOCAL_SUPABASE_PROJECT_ID) || trim(env.VITE_SUPABASE_PROJECT_ID),
  }
}

function readViteEnv(): EveEnv {
  const env: EveEnv = {}
  for (const key of ENV_KEYS) {
    env[key] = import.meta.env[key] as string | undefined
  }
  return env
}

export const eveSupabase = resolveEveSupabaseTarget(readViteEnv())
export const EVE_BACKEND = eveSupabase.backend
export const SUPABASE_URL = eveSupabase.url
export const SUPABASE_ANON_KEY = eveSupabase.anonKey
export const SUPABASE_PROJECT_ID = eveSupabase.projectId

/** True only for a loopback Supabase URL. Hosted staging and production stay false. */
export function isLoopbackSupabaseUrl(url: string | undefined): boolean {
  try {
    const host = new URL(url ?? '').hostname.replace(/^\[|\]$/g, '')
    return host === 'localhost' || host === '127.0.0.1' || host === '::1'
  } catch {
    return false
  }
}
